import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Table,
  Row,
  Col,
  Container,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";

const InnovationsOne = () => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  // State
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [abhiyanList, setAbhiyanList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);
  const [selectedAbhiyan, setSelectedAbhiyan] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [warning, setWarning] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("InnovationOne");

  // Safe array utility
  const safeArray = (json) =>
    Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];

  /* ---------------------------------------------------
      🔵 HANDLE REPORT CHANGE
  --------------------------------------------------- */
  const handleFilterChange = (event) => {
    const selected = event.target.value;
    setSelectedFilter(selected);

    if (selected === "Innovations") navigate("/innovations");
    if (selected === "InnovationOne") navigate("/innovationsOne");
    if (selected === "Innovationtwo") navigate("/innovationsTwo");
  };

  // CheckboxDropdown (copied and adapted from Innovations.js)
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const toggle = () => {
      if (!disabled) {
        setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
      }
    };

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>
        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={toggle}
        >
          {selected.length === options.length && options.length > 0
            ? "All Selected"
            : selected.length > 0
            ? `${selected.length} Selected`
            : "All"}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{
              zIndex: 9999,
              maxHeight: 220,
              overflowY: "auto",
              bottom: "100%",
              marginBottom: "8px",
            }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={options.length > 0 && selected.length === options.length}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? options.map((o) => o[idKey]) : []
                  )
                }
              />
            </div>

            {options.map((o) => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() => {
                    // Taluka limit
                    if (
                      dropdownKey === "taluka" &&
                      !selected.includes(o[idKey]) &&
                      selected.length >= 3
                    ) {
                      setWarning("Please select a maximum of 3 Talukas");
                      return;
                    }
                    setWarning("");
                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter((x) => x !== o[idKey])
                        : [...selected, o[idKey]]
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  // Calculate totals (adapted for new data)
  const calculateTotals = (items) => {
    let totals = {
      tbl_crop_shop: 0,
      tbl_demo_house_taluka: 0,
      tbl_sand_bank: 0,
      tbl_gharkul_mart: 0,
    };
    items.forEach((item) => {
      totals.tbl_crop_shop += +item.tbl_crop_shop || 0;
      totals.tbl_demo_house_taluka += +item.tbl_demo_house_taluka || 0;
      totals.tbl_sand_bank += +item.tbl_sand_bank || 0;
      totals.tbl_gharkul_mart += +item.tbl_gharkul_mart || 0;
    });
    return totals;
  };


  // Initial load
  useEffect(() => {
    fetch(`${apiBaseUrl}/innovation/districts`)
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTableData(arr);
        setDistrictList(arr);
      });

    fetch(`${apiBaseUrl}/innovation/abhiyans`)
      .then((r) => r.json())
      .then((json) => setAbhiyanList(safeArray(json)));
  }, [apiBaseUrl]);

  // Abhiyan → District
  useEffect(() => {
    if (!selectedAbhiyan.length) return;
    fetch(
      `${apiBaseUrl}/innovation/abhiyans/${selectedAbhiyan.join(",")}/districts`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setDistrictList(arr);
        setTableData(arr);
        setSelectedDistrict([]);
        setSelectedTaluka([]);
        setTalukaList([]);
      });
  }, [selectedAbhiyan, apiBaseUrl]);

  // District → Taluka
  useEffect(() => {
    if (!selectedDistrict.length || !selectedAbhiyan.length) return;
    fetch(
      `${apiBaseUrl}/innovation/abhiyans/${selectedAbhiyan.join(",")}/districts/${selectedDistrict.join(",")}/talukas`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTalukaList(arr);
        setTableData(arr);
        setSelectedTaluka([]);
      });
  }, [selectedDistrict, selectedAbhiyan, apiBaseUrl]);

  // Taluka → Show only selected taluka data, stop further drilldown
  useEffect(() => {
    if (
      selectedTaluka.length > 0 &&
      talukaList.length > 0
    ) {
      // Filter talukaList to only selected talukas
      const filtered = talukaList.filter((t) => selectedTaluka.includes(t.taluka_id));
      setTableData(filtered);
    }
  }, [selectedTaluka, talukaList]);

  // Refresh
  const handleRefresh = () => {
    setSelectedAbhiyan([]);
    setSelectedDistrict([]);
    setSelectedTaluka([]);
    setDistrictList([]);
    setTalukaList([]);
    fetch(`${apiBaseUrl}/innovation/districts`)
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTableData(arr);
        setDistrictList(arr);
      });
  };

  // Export Excel
  const exportToExcel = () => {
    const areaTitle = selectedTaluka.length > 0
      ? "तालुका"
      : selectedDistrict.length > 0
      ? "तालुका"
      : "जिल्हा";
    const areaName = (row) =>
      selectedTaluka.length > 0
        ? row.taluka
        : selectedDistrict.length > 0
        ? row.taluka
        : row.district;

    const rows = filteredView.map((r, i) => ({
      "अ. क्र": i + 1,
      [areaTitle]: areaName(r),
      "कॉप शॉप": r.tbl_crop_shop || 0,
      "डेमो हाऊस तालुका": r.tbl_demo_house_taluka || 0,
      "सॅण्ड बँक": r.tbl_sand_bank || 0,
      "घरकुल मार्ट": r.tbl_gharkul_mart || 0,
    }));

    rows.push({
      "अ. क्र": "",
      [areaTitle]: "Total",
      "कॉप शॉप": totals.tbl_crop_shop,
      "डेमो हाऊस तालुका": totals.tbl_demo_house_taluka,
      "सॅण्ड बँक": totals.tbl_sand_bank,
      "घरकुल मार्ट": totals.tbl_gharkul_mart,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Innovation Report 1");
    XLSX.writeFile(wb, "Innovation Report 1.xlsx");
  };

  // Search
  const filteredView = tableData.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals(filteredView);

  return (
    <Container fluid>
      <Row>
        {/* Sidebar */}
        <Col md={2} className="p-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/")}
            className="mb-3 w-100"
            style={{ borderRadius: "6px", fontWeight: 500 }}
          >
            Back
          </Button>

          <div
            className="filters p-3 shadow-sm"
            style={{ backgroundColor: "#EAF0F9", position: "sticky", top: "10px", zIndex: 10000 }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 style={{ fontWeight: "bold", color: "#1F3C88" }}>Filters</h5>
              <div
                title="Refresh"
                onClick={handleRefresh}
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            {/* REPORT SELECT */}
            <Form.Group className="mb-3">
              <Form.Label>Select Report</Form.Label>
              <Form.Select value={selectedFilter} onChange={handleFilterChange}>
                <option value="Innovations">भूमिहीन लाभार्थ्यांना जागा उपलब्ध करून देण्यासाठीचे नाविन्यपूर्ण उपक्रम</option>
                <option value="InnovationOne">घरकुले वेळेत पूर्ण होण्यासाठीचे नाविन्यपूर्ण उपक्रम</option>
                {/* <option value="Innovationtwo">इतर नाविन्यपूर्ण उपक्रम</option> */}
              </Form.Select>
            </Form.Group>

            <CheckboxDropdown
              label="अभियान"
              options={abhiyanList}
              idKey="abhiyan_id"
              nameKey="abhiyan_name"
              selected={selectedAbhiyan}
              setSelected={setSelectedAbhiyan}
              dropdownKey="abhiyan"
            />

            <CheckboxDropdown
              label="जिल्हा"
              options={districtList}
              idKey="district_id"
              nameKey="district"
              selected={selectedDistrict}
              setSelected={setSelectedDistrict}
              disabled={!districtList.length}
              dropdownKey="district"
            />

            <CheckboxDropdown
              label="तालुका"
              options={talukaList}
              idKey="taluka_id"
              nameKey="taluka"
              selected={selectedTaluka}
              setSelected={setSelectedTaluka}
              disabled={!talukaList.length}
              dropdownKey="taluka"
            />
          </div>
        </Col>

        {/* Main Section */}
        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
            <h5 className="fw-bold mb-0" style={{ color: "#1F3C88" }}>
              घरकुले वेळेत पुर्ण होण्यासाठीचे नाविन्यपुर्ण उपक्रम
            </h5>

            <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
              <Form.Group controlId="searchBox" style={{ width: '250px' }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: '6px',
                    height: '38px',
                    fontSize: '14px',
                    border: '1px solid #ced4da',
                  }}
                />
              </Form.Group>

              <Button
                style={{
                  backgroundColor: "#4da6ff",
                  borderColor: "#4da6ff",
                }}
                onClick={exportToExcel}
              >
                Export to Excel
              </Button>
            </div>
          </div>

          {warning && <Alert variant="warning">{warning}</Alert>}

          <div
            style={{
              maxHeight: "500px",
              overflowY: "auto",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
            }}
          >
            <Table striped bordered hover responsive>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f8f9fa",
                  zIndex: 5,
                }}
              >
                <tr>
                  <th>अ. क्र.</th>
                  <th>
                    {selectedTaluka.length > 0
                      ? "तालुका"
                      : selectedDistrict.length > 0
                      ? "तालुका"
                      : "जिल्हा"}
                  </th>
                  <th>कॉप शॉप</th>
                  <th>डेमो हाऊस तालुका</th>
                  <th>सॅण्ड बँक</th>
                  <th>घरकुल मार्ट</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#eef3ff", fontWeight: "bold" }}>
                  <td colSpan={2} className="text-center align-middle">Total</td>
                  <td className="text-center align-middle">{totals.tbl_crop_shop}</td>
                  <td className="text-center align-middle">{totals.tbl_demo_house_taluka}</td>
                  <td className="text-center align-middle">{totals.tbl_sand_bank}</td>
                  <td className="text-center align-middle">{totals.tbl_gharkul_mart}</td>
                </tr>

                {filteredView.map((r, i) => (
                  <tr key={i}>
                    <td className="text-center align-middle">{i + 1}</td>
                    <td className="text-center align-middle">
                      {selectedTaluka.length > 0
                        ? r.taluka
                        : selectedDistrict.length > 0
                        ? r.taluka
                        : r.district}
                    </td>
                    <td className="text-center align-middle">{r.tbl_crop_shop}</td>
                    <td className="text-center align-middle">{r.tbl_demo_house_taluka}</td>
                    <td className="text-center align-middle">{r.tbl_sand_bank}</td>
                    <td className="text-center align-middle">{r.tbl_gharkul_mart}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default InnovationsOne;
